import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileVideo, FlaskConical, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyDialog,
  TaskyIconButton,
  TaskyInput,
  TaskySpinner,
  TaskySwitch,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
  TaskyTabs,
  TaskyTextarea,
  useConfirm,
} from '../tasky';
import adminVideoStyleRecipeService, {
  type AdminVideoStyleRecipe,
  type AdminVideoStyleRecipeDetail,
  type VideoStyleTrialCounts,
  videoStyleErrorMessages,
} from '../../services/api/adminVideoStyleRecipeService';
import VideoStyleSampleSection from './VideoStyleSampleSection';
import VideoStyleTrialDialog from './VideoStyleTrialDialog';
import VideoStyleTrialSection from './VideoStyleTrialSection';
import VideoStyleTrialsTab from './VideoStyleTrialsTab';
import { useVisiblePolling } from './useVisiblePolling';

/** backend recipe_video.analysis.max_upload_mb と同じ */
const MAX_UPLOAD_MB = 50;
const POLL_MS = 5000;

const ANALYSIS_LABEL: Record<string, { label: string; variant: 'secondary' | 'info' | 'success' | 'destructive' | 'warning' }> = {
  queued: { label: '分解待ち', variant: 'info' },
  running: { label: '分解中', variant: 'info' },
  done: { label: '分解済み', variant: 'success' },
  failed: { label: '分解失敗', variant: 'destructive' },
};

const isAnalyzing = (item: AdminVideoStyleRecipe) => item.analysis_status === 'queued' || item.analysis_status === 'running';

/** 見本の列だけ（詳細の編集中の名前・JSON を上書きしない） */
const sampleFields = (item: AdminVideoStyleRecipe) => ({
  thumbnail_url: item.thumbnail_url,
  sample_video_url: item.sample_video_url,
  sample_status: item.sample_status,
  sample_error: item.sample_error,
  sample_project_id: item.sample_project_id,
});

/** 試作できるか（分解が終わり、生成に使えるレシピ。下書きでも試せる） */
type PanelTab = 'styles' | 'trials';

const canTry = (item: AdminVideoStyleRecipe) => !isAnalyzing(item) && item.analysis_status !== 'failed' && item.errors.length === 0;

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('ja-JP') : '—');

const seconds = (value: number) => `${value.toFixed(1)}s`;

/**
 * 管理画面「動画スタイル」。参考動画から動画スタイルレシピを作り（分解は非同期）、公開を切り替える。
 * 公開中のスタイルだけがユーザーの「レシピ動画」の選択肢に出る。
 */
const VideoStyleRecipePanel: React.FC = () => {
  const confirm = useConfirm();
  const [items, setItems] = useState<AdminVideoStyleRecipe[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState<string[]>([]);

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [detail, setDetail] = useState<AdminVideoStyleRecipeDetail | null>(null);
  const [form, setForm] = useState({ name: '', description: '', json: '' });
  const [detailErrors, setDetailErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const [trialTarget, setTrialTarget] = useState<AdminVideoStyleRecipe | null>(null);
  const [trialRefresh, setTrialRefresh] = useState(0);

  // タブは ?tab=trials で開き直せるようにする（/admin/video-style-recipes のまま）
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: PanelTab = searchParams.get('tab') === 'trials' ? 'trials' : 'styles';
  const [trialCounts, setTrialCounts] = useState<VideoStyleTrialCounts>({ running: 0, failed: 0, all: 0 });

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setIsLoading(true);
    try {
      setItems(await adminVideoStyleRecipeService.list());
    } catch {
      if (!quiet) setErrors(['読み込めませんでした。']);
    } finally {
      if (!quiet) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // 分解中のものがある間は一覧を取り直す
  const analyzing = items.some(isAnalyzing);
  useEffect(() => {
    if (!analyzing) return undefined;
    const timer = window.setInterval(() => void load(true), POLL_MS);
    return () => window.clearInterval(timer);
  }, [analyzing, load]);

  const replace = (updated: AdminVideoStyleRecipe) => setItems((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));

  const upload = async () => {
    if (!uploadFile || !uploadName.trim()) return;
    if (uploadFile.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setErrors([`動画は ${MAX_UPLOAD_MB}MB までです。`]);
      return;
    }
    setIsUploading(true);
    setErrors([]);
    try {
      await adminVideoStyleRecipeService.create({ name: uploadName.trim(), description: uploadDescription.trim() || undefined, file: uploadFile });
      setIsUploadOpen(false);
      setUploadName('');
      setUploadDescription('');
      setUploadFile(null);
      await load();
    } catch (error) {
      setErrors(videoStyleErrorMessages(error, 'アップロードできませんでした。'));
    } finally {
      setIsUploading(false);
    }
  };

  const toggle = async (item: AdminVideoStyleRecipe) => {
    setErrors([]);
    try {
      replace(await adminVideoStyleRecipeService.toggle(item.id));
    } catch (error) {
      setErrors(videoStyleErrorMessages(error, '切り替えられませんでした。'));
    }
  };

  const remove = async (item: AdminVideoStyleRecipe) => {
    if (!(await confirm({ title: `「${item.name}」を削除しますか？`, description: '参考動画と分解結果も消えます。', confirmText: '削除', tone: 'destructive' }))) return;
    setErrors([]);
    try {
      await adminVideoStyleRecipeService.remove(item.id);
      await load();
    } catch (error) {
      setErrors(videoStyleErrorMessages(error, '削除できませんでした。'));
    }
  };

  const openDetail = async (item: AdminVideoStyleRecipe) => {
    setDetailErrors([]);
    try {
      const data = await adminVideoStyleRecipeService.show(item.id);
      setDetail(data);
      setForm({ name: data.name, description: data.description ?? '', json: JSON.stringify(data.recipe, null, 2) });
    } catch {
      setErrors(['詳細を読み込めませんでした。']);
    }
  };

  const save = async () => {
    if (!detail) return;
    let recipe: Record<string, unknown>;
    try {
      recipe = JSON.parse(form.json);
    } catch {
      setDetailErrors(['JSON として読めません。括弧・カンマを確かめてください。']);
      return;
    }
    setIsSaving(true);
    setDetailErrors([]);
    try {
      const updated = await adminVideoStyleRecipeService.update(detail.id, {
        name: form.name.trim(),
        description: form.description.trim() || null,
        ...(isAnalyzing(detail) ? {} : { recipe }),
      });
      setDetail(updated);
      setForm({ name: updated.name, description: updated.description ?? '', json: JSON.stringify(updated.recipe, null, 2) });
      replace(updated);
    } catch (error) {
      setDetailErrors(videoStyleErrorMessages(error, '保存できませんでした。'));
    } finally {
      setIsSaving(false);
    }
  };

  const reanalyze = async () => {
    if (!detail) return;
    setDetailErrors([]);
    try {
      replace(await adminVideoStyleRecipeService.reanalyze(detail.id));
      setDetail(null);
    } catch (error) {
      setDetailErrors(videoStyleErrorMessages(error, '分解し直せませんでした。'));
    }
  };

  const changeTab = (value: PanelTab) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'trials') next.set('tab', 'trials');
    else next.delete('tab');
    setSearchParams(next);
  };

  // タブのバッジ（作成中の件数）。「試作」タブを開いている間はタブ側が取り直して知らせる
  const loadTrialCounts = useCallback(async () => {
    try {
      setTrialCounts((await adminVideoStyleRecipeService.trialList({ status: 'running' })).counts);
    } catch {
      // バッジが出ないだけ
    }
  }, []);
  useEffect(() => {
    if (tab === 'styles') void loadTrialCounts();
  }, [tab, loadTrialCounts, trialRefresh]);
  useVisiblePolling(tab === 'styles' && trialCounts.running > 0, loadTrialCounts);

  const openStyle = (styleId: number) => {
    changeTab('styles');
    const target = items.find((item) => item.id === styleId);
    if (target) void openDetail(target);
  };

  const trialStarted = (target: AdminVideoStyleRecipe) => {
    setTrialTarget(null);
    setTrialRefresh((n) => n + 1);
    // 一覧から始めたら、そのスタイルの詳細を開いて進捗を見せる
    if (detail?.id !== target.id) void openDetail(target);
  };

  // 見本の作成・取り外し・作成中の取り直し。見本の列だけを詳細と一覧に反映する
  const sampleChanged = useCallback((updated: AdminVideoStyleRecipe) => {
    setDetail((prev) => (prev && prev.id === updated.id ? { ...prev, ...sampleFields(updated) } : prev));
    setItems((prev) => prev.map((row) => (row.id === updated.id ? { ...row, ...sampleFields(updated) } : row)));
  }, []);
  const detailId = detail?.id;
  const pollSample = useCallback(async () => {
    if (detailId === undefined) return;
    try {
      sampleChanged(await adminVideoStyleRecipeService.show(detailId));
    } catch {
      // 次の取り直しで直る
    }
  }, [detailId, sampleChanged]);
  useVisiblePolling(detail?.sample_status === 'processing', pollSample);

  const shots = detail?.analysis?.shots ?? [];

  return (
    <div className="flex flex-col gap-4 rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6" data-testid="video-style-recipe-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-dark">動画スタイル</h3>
          <p className="text-sm text-gray-500">公開中のスタイルだけがユーザーの選択肢に出ます。</p>
        </div>
        {tab === 'styles' && (
          <TaskyButton size="sm" onClick={() => setIsUploadOpen(true)}>
            <Plus aria-hidden />
            参考動画から作る
          </TaskyButton>
        )}
      </div>

      <TaskyTabs
        aria-label="動画スタイルの表示"
        value={tab}
        onChange={(value) => changeTab(value as PanelTab)}
        items={[
          { value: 'styles', label: 'スタイル' },
          { value: 'trials', label: '試作', count: trialCounts.running },
        ]}
      />

      {errors.length > 0 && (
        <TaskyAlert tone="error">
          {errors.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </TaskyAlert>
      )}

      {tab === 'trials' ? (
        <VideoStyleTrialsTab onCounts={setTrialCounts} onOpenStyle={openStyle} />
      ) : isLoading ? (
        <TaskySpinner />
      ) : items.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500">まだスタイルがありません。</p>
      ) : (
        <div className="overflow-x-auto">
          <TaskyTable>
            <TaskyTableHead>
              <TaskyTableRow>
                <TaskyTableHeaderCell>名前</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>公開</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>分解</TaskyTableHeaderCell>
                <TaskyTableHeaderCell className="whitespace-nowrap">作成元動画</TaskyTableHeaderCell>
                <TaskyTableHeaderCell className="whitespace-nowrap">シーン</TaskyTableHeaderCell>
                <TaskyTableHeaderCell className="whitespace-nowrap">作成日</TaskyTableHeaderCell>
                <TaskyTableHeaderCell className="w-24" />
              </TaskyTableRow>
            </TaskyTableHead>
            <TaskyTableBody>
              {items.map((item) => {
                const analysis = item.analysis_status ? ANALYSIS_LABEL[item.analysis_status] : null;
                const blocked = item.status === 'draft' && (isAnalyzing(item) || item.analysis_status === 'failed' || item.errors.length > 0);
                return (
                  <TaskyTableRow key={item.id} data-testid="video-style-recipe-row">
                    <TaskyTableCell>
                      <p className="font-semibold text-dark">{item.name}</p>
                      {item.description && <p className="line-clamp-2 max-w-md text-xs text-gray-500">{item.description}</p>}
                      {item.analysis_error && <p className="mt-1 line-clamp-2 max-w-md whitespace-pre-line text-xs text-red-600">{item.analysis_error}</p>}
                    </TaskyTableCell>
                    <TaskyTableCell>
                      <TaskySwitch
                        checked={item.status === 'published'}
                        disabled={blocked}
                        onChange={() => void toggle(item)}
                        aria-label={`${item.name}を公開する`}
                      />
                    </TaskyTableCell>
                    <TaskyTableCell>
                      {analysis ? (
                        <TaskyBadge variant={analysis.variant} dot={isAnalyzing(item)}>
                          {analysis.label}
                        </TaskyBadge>
                      ) : (
                        <TaskyBadge variant="secondary">手書き</TaskyBadge>
                      )}
                      {item.agreement && item.agreement.judged > 0 && (
                        <p className="mt-1 whitespace-nowrap text-xs text-gray-500">
                          Clef 一致 {item.agreement.agree}/{item.agreement.judged}
                        </p>
                      )}
                    </TaskyTableCell>
                    <TaskyTableCell>
                      {item.source_video_url ? (
                        <a href={item.source_video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-primary hover:underline">
                          <FileVideo className="h-4 w-4" aria-hidden />
                          参考動画
                        </a>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </TaskyTableCell>
                    <TaskyTableCell>{item.scene_count}</TaskyTableCell>
                    <TaskyTableCell className="whitespace-nowrap">{formatDate(item.created_at)}</TaskyTableCell>
                    <TaskyTableCell>
                      <span className="flex gap-1">
                        <TaskyIconButton size="sm" variant="ghost" aria-label={`${item.name}を試しに作る`} onClick={() => setTrialTarget(item)} disabled={!canTry(item)}>
                          <FlaskConical />
                        </TaskyIconButton>
                        <TaskyIconButton size="sm" variant="ghost" aria-label={`${item.name}を開く`} onClick={() => void openDetail(item)}>
                          <Pencil />
                        </TaskyIconButton>
                        <TaskyIconButton size="sm" variant="ghost" aria-label={`${item.name}を削除`} onClick={() => void remove(item)} disabled={isAnalyzing(item)}>
                          <Trash2 />
                        </TaskyIconButton>
                      </span>
                    </TaskyTableCell>
                  </TaskyTableRow>
                );
              })}
            </TaskyTableBody>
          </TaskyTable>
        </div>
      )}

      <TaskyDialog
        open={isUploadOpen}
        onClose={() => !isUploading && setIsUploadOpen(false)}
        title="参考動画から作る"
        footer={
          <div className="flex justify-end gap-2">
            <TaskyButton variant="ghost" onClick={() => setIsUploadOpen(false)} disabled={isUploading}>
              やめる
            </TaskyButton>
            <TaskyButton onClick={() => void upload()} disabled={isUploading || !uploadFile || !uploadName.trim()}>
              {isUploading ? 'アップロード中…' : '分解を始める'}
            </TaskyButton>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm font-medium">
            名前
            <TaskyInput value={uploadName} maxLength={100} onChange={(e) => setUploadName(e.target.value)} placeholder="漫画風（課題→気づき→デモ）" />
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium">
            メモ（任意）
            <TaskyInput value={uploadDescription} maxLength={1000} onChange={(e) => setUploadDescription(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium">
            参考動画（mp4・{MAX_UPLOAD_MB}MB・3分まで）
            <input
              type="file"
              accept="video/mp4,.mp4"
              onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
              data-testid="video-style-file"
              className="text-sm font-normal"
            />
          </label>
        </div>
      </TaskyDialog>

      <TaskyDialog
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={detail?.name ?? ''}
        size="xl"
        footer={
          <div className="flex flex-wrap justify-between gap-2">
            <span className="flex flex-wrap gap-2">
              {detail && (
                <TaskyButton variant="outline" onClick={() => setTrialTarget(detail)} disabled={!canTry(detail)}>
                  <FlaskConical aria-hidden />
                  試しに作る
                </TaskyButton>
              )}
              {detail?.source_video_url && detail.status === 'draft' && !isAnalyzing(detail) && (
                <TaskyButton variant="ghost" onClick={() => void reanalyze()}>
                  <RefreshCw aria-hidden />
                  分解し直す
                </TaskyButton>
              )}
            </span>
            <span className="flex gap-2">
              <TaskyButton variant="ghost" onClick={() => setDetail(null)}>
                閉じる
              </TaskyButton>
              <TaskyButton onClick={() => void save()} disabled={isSaving || !form.name.trim()}>
                保存
              </TaskyButton>
            </span>
          </div>
        }
      >
        {detail && (
          <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto pr-1">
            {detailErrors.length > 0 && (
              <TaskyAlert tone="error" data-testid="video-style-detail-errors">
                {detailErrors.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </TaskyAlert>
            )}
            {detail.errors.length > 0 && detailErrors.length === 0 && (
              <TaskyAlert tone="warning" title="公開する前に直す点">
                {detail.errors.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </TaskyAlert>
            )}
            <label className="flex flex-col gap-1 text-sm font-medium">
              名前
              <TaskyInput value={form.name} maxLength={100} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium">
              説明
              <TaskyTextarea rows={2} value={form.description} maxLength={1000} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </label>

            <VideoStyleSampleSection recipe={detail} onChange={sampleChanged} />

            <VideoStyleTrialSection recipeId={detail.id} refreshKey={trialRefresh} onSampleStarted={sampleChanged} />

            {shots.length > 0 && (
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium">
                  分解結果（{shots.length} ショット{detail.analysis?.duration ? `・${seconds(detail.analysis.duration)}` : ''}）
                </p>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="video-style-shots">
                  {shots.map((shot, i) => (
                    <div key={`${shot.start}-${i}`} className="flex flex-col gap-1 rounded-tasky-sm border border-warm-border p-1.5 text-xs">
                      <div className="aspect-[9/16] overflow-hidden rounded bg-warm-surface">
                        {shot.frame_url && <img src={shot.frame_url} alt="" className="h-full w-full object-cover" loading="lazy" />}
                      </div>
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-gray-500">
                          {seconds(shot.start)}–{seconds(shot.end)}
                        </span>
                        {shot.agree === false && <TaskyBadge variant="warning">要確認</TaskyBadge>}
                      </div>
                      <span className="font-medium">
                        {shot.role} / {shot.kind}
                      </span>
                      {shot.clef && shot.agree === false && (
                        <span className="text-amber-700">
                          Clef: {shot.clef.kind} / {shot.clef.text_style}
                        </span>
                      )}
                      {shot.speech && shot.speech.length > 0 && <span className="line-clamp-2 text-gray-600">{shot.speech.map((s) => s.text).join(' ')}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <label className="flex flex-col gap-1 text-sm font-medium">
              レシピ（JSON）
              <TaskyTextarea
                rows={16}
                value={form.json}
                onChange={(e) => setForm({ ...form, json: e.target.value })}
                className="font-mono text-xs"
                spellCheck={false}
                disabled={isAnalyzing(detail)}
                data-testid="video-style-json"
              />
            </label>
          </div>
        )}
      </TaskyDialog>

      <VideoStyleTrialDialog recipe={trialTarget} onClose={() => setTrialTarget(null)} onStarted={() => trialTarget && trialStarted(trialTarget)} />
    </div>
  );
};

export default VideoStyleRecipePanel;
